<template>
	<div class="row room-overview">
		<div v-for="room in items" :key="room.ID" class="room filter-item">
			<div class="row">
				<div class="columns column12 room-image">
					<picture>
						<source v-if="room.imageOverviewWebp" :srcset="room.imageOverviewWebp" type="image/webp" />
						<source :srcset="room.imageOverview" />
						<img :src="room.imageOverview" :alt="room.imageOverview.imageAlt" loading="lazy" />
					</picture>
				</div>
				<div class="columns column6 room-content">
					<div class="contentblock-content">
						<h2>{{ room.header }}</h2>
						<div v-parse-links v-html="room.overviewContent" />
						<div class="room-icons">
							<div class="icon-holder">
								<font-awesome-icon icon="fa fa-users" size="1x" />
								<span>{{ room.pax }}</span>
							</div>
							<div class="icon-holder">
								<font-awesome-icon icon="fa fa-arrows" size="1x" />
								<span>{{ room.surface }}</span>
							</div>
							<div class="icon-holder">
								<font-awesome-icon icon="fa fa-euro-sign" size="1x" />
								<span>{{ room.price }} </span>
							</div>
						</div>
						<p>
							<nuxt-link :to="room.filename" class="button no-fill">
								{{ $t('viewThisRoom') }}
							</nuxt-link>
							<book-button v-if="type === 'rooms'" class="button cta">
								{{ $t('bookThisRoom') }}
							</book-button>
							<nuxt-link v-else :to="localePath('meetingform', locale)" class="button cta">
								{{ $t('buttonRequestInfo') }}
							</nuxt-link>
						</p>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup>
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';

const { locale } = useI18n();
const localePath = useLocalePath();

defineProps({
	items: { type: Array, default: () => [] },
	filter: { type: String, default: 'showAll' },
	type: { type: String, default: 'rooms' },
});

onMounted(() => {
	Fancybox.bind('[data-fancybox]', {});
});
</script>

<style lang="scss" scoped>
.room-overview {
	padding: 0 20px;
}

.room {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	padding: 20px 40px;
	margin: 40px auto;
	width: 100%;
	position: relative;

	&::before {
		content: '';
		display: block;
		position: absolute;
		inset: 40px;
		z-index: 3;
		border: 1px solid #fff;
	}

	&:nth-child(odd) {
		flex-direction: row-reverse;
	}

	.button.no-fill,
	:deep(.button.no-fill) {
		border-color: #fff;
		color: #fff;

		&:hover {
			background: #fff;
			color: var(--cta-color-hover);
		}
	}

	p .button {
		margin: 0 5px 5px 0;
	}

	.columns:first-child {
		padding: 0;
		position: absolute;
		inset: 0;
		z-index: 1;

		&::before {
			content: '';
			display: block;
			position: absolute;
			inset: 0;
			z-index: 2;
			background: rgb(0 0 0 / 40%);
		}
	}

	.column6 {
		position: relative;
		z-index: 4;
		color: #fff;
		margin: 150px 0;

		p,
		:deep(p),
		h2,
		:deep(h2),
		h3,
		:deep(h3) {
			color: #fff;
		}
	}

	img {
		object-fit: cover;
		width: 100%;
		height: 100%;
		position: relative;
		z-index: 1;
	}

	.room-content {
		padding: 20px 50px;
	}
}

.room-icons {
	display: flex;
	flex-flow: row wrap;
	justify-content: flex-start;
	align-items: center;

	.icon-holder {
		display: flex;
		flex-flow: row nowrap;
		justify-content: space-between;
		align-items: center;
		width: auto;
		margin: 6px 15px 6px 0;

		span {
			display: flex;
			flex-flow: row nowrap;
			justify-content: flex-start;
			align-items: center;
		}

		svg {
			margin: 0 12px 0 0;
			font-size: calc(var(--body-font-size) + 3px);
		}
	}
}

@media (max-width: 1080px) {
	.room {
		padding: 20px 10px;

		.column6 {
			margin: 50px 0;
		}

		&::before {
			inset: 10px;
		}

		.room-content {
			margin: 20px 0;
		}
	}
}

@media (max-width: 580px) {
	.room {
		margin-bottom: 25px;

		.room-content {
			padding: 0 20px;
		}
	}
}
</style>
